import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, Mail, Save, Send } from "lucide-react";
import { toast } from "sonner";

export default function AdminSmtpPage() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/smtp/konfiguration");
        setCfg(data);
      } catch (e) { toast.error(errMsg(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const { data } = await api.put("/smtp/konfiguration", cfg);
      setCfg(data);
      toast.success("SMTP-Konfiguration gespeichert.");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const test = async () => {
    if (!testTo.trim()) { toast.error("Empfänger fehlt."); return; }
    setTesting(true);
    try {
      const { data } = await api.post("/smtp/test", { an: testTo });
      if (data.erfolg) toast.success(data.meldung);
      else toast.error(data.meldung);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setTesting(false); }
  };

  if (loading || !cfg) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: "var(--font-heading)" }}>SMTP-Konfiguration</h1>
        <p className="text-sm text-muted-foreground">
          Für E-Mail-Benachrichtigungen an Lieferanten (Dokumente, Galerien, Umfragen).
          Standardmäßig deaktiviert – In-App-Benachrichtigungen funktionieren unabhängig davon.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Mail className="h-5 w-5 text-primary" /> SMTP-Server
          </CardTitle>
          <CardDescription>Zugangsdaten des ausgehenden Mailservers.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-2 rounded-md border p-3 bg-secondary/40 cursor-pointer">
            <Switch checked={!!cfg.aktiv}
              onCheckedChange={(v) => setCfg({ ...cfg, aktiv: v })}
              data-testid="smtp-aktiv" />
            <div>
              <p className="text-sm font-medium">E-Mail-Versand aktiviert</p>
              <p className="text-xs text-muted-foreground">Wenn aus, werden Mails nur geloggt.</p>
            </div>
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <Label className="text-sm">Host</Label>
              <Input value={cfg.host || ""} placeholder="smtp.example.com"
                onChange={(e) => setCfg({ ...cfg, host: e.target.value })}
                data-testid="smtp-host" />
            </div>
            <div>
              <Label className="text-sm">Port</Label>
              <Input type="number" value={cfg.port || 587}
                onChange={(e) => setCfg({ ...cfg, port: Number(e.target.value) })}
                data-testid="smtp-port" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Benutzer</Label>
              <Input value={cfg.benutzer || ""}
                onChange={(e) => setCfg({ ...cfg, benutzer: e.target.value })}
                data-testid="smtp-user" />
            </div>
            <div>
              <Label className="text-sm">Passwort</Label>
              <Input type="password" value={cfg.passwort || ""}
                placeholder={cfg.passwort === "***" ? "(unverändert)" : ""}
                onChange={(e) => setCfg({ ...cfg, passwort: e.target.value })}
                data-testid="smtp-pass" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Absender-Adresse</Label>
              <Input type="email" value={cfg.absender_email || ""} placeholder="info@seifried.app"
                onChange={(e) => setCfg({ ...cfg, absender_email: e.target.value })}
                data-testid="smtp-from" />
            </div>
            <div>
              <Label className="text-sm">Absender-Name</Label>
              <Input value={cfg.absender_name || ""}
                onChange={(e) => setCfg({ ...cfg, absender_name: e.target.value })}
                data-testid="smtp-fromname" />
            </div>
          </div>
          <label className="flex items-center gap-2">
            <Switch checked={!!cfg.tls}
              onCheckedChange={(v) => setCfg({ ...cfg, tls: v })}
              data-testid="smtp-tls" />
            <span className="text-sm">STARTTLS (Port 587). Deaktivieren nur bei Port 465 (SSL/TLS).</span>
          </label>
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="smtp-save">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Speichern
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Send className="h-5 w-5 text-primary" /> Testmail
          </CardTitle>
          <CardDescription>Sendet eine Testmail mit der aktuellen Konfiguration.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-sm">Empfänger</Label>
            <Input type="email" placeholder="empfaenger@example.com"
              value={testTo} onChange={(e) => setTestTo(e.target.value)}
              data-testid="smtp-test-to" />
          </div>
          <Button onClick={test} disabled={testing || !cfg.aktiv} data-testid="smtp-test-btn">
            {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Testmail senden
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
